import { classes } from 'html-classes';
import {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { usePhoneInput } from '~/hooks/usePhoneInput';

import { PHONE_INPUT_NODE_ID, PHONE_INPUT_PLACEHOLDER } from './constants';
import { PhoneInputProps } from './interfaces';
import styles from './PhoneInput.module.scss';

/**
 * Checks if backspace should be prevented when cursor is right after the plus sign.
 */
const shouldPreventBackspaceAtPlus = (
  key: string,
  selectionStart: number,
  selectionEnd: number,
): boolean => {
  return key === 'Backspace' && selectionStart === 1 && selectionEnd === 1;
};

/**
 * Checks if delete should be prevented when cursor is at the very start.
 */
const shouldPreventDeleteAtStart = (
  key: string,
  selectionStart: number,
  selectionEnd: number,
): boolean => {
  return key === 'Delete' && selectionStart === 0 && selectionEnd === 0;
};

/**
 * Checks if a selection deletion includes the plus sign and needs special handling.
 */
const shouldHandleSelectionWithPlus = (
  key: string,
  selectionStart: number,
  selectionEnd: number,
): boolean => {
  return (
    (key === 'Backspace' || key === 'Delete') &&
    selectionStart === 0 &&
    selectionEnd > 0
  );
};

/**
 * Phone number input component with international format support.
 * Automatically handles the leading plus sign and validates phone numbers.
 * Supports both controlled and uncontrolled modes.
 */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value: controlledValue,
      onChange: onControlledChange,
      onValidate,
      error,
      showError,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(controlledValue || '');

    const isControlled = controlledValue !== undefined;

    const currentValue = isControlled ? controlledValue : internalValue;

    const handleValueChange = (newValue: string) => {
      if (isControlled && onControlledChange) {
        onControlledChange(newValue);
      } else {
        setInternalValue(newValue);
      }
    };

    const { handleChange, handleKeyPress, handlePaste, validationError } =
      usePhoneInput({
        value: currentValue,
        onValidate,
        onChange: handleValueChange,
      });

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (isControlled) {
        setInternalValue(controlledValue);
      }
    }, [controlledValue, isControlled]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const setSelectionWithDelay = (
      target: HTMLInputElement,
      position: number,
    ) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        target.setSelectionRange(position, position);
      }, 0);
    };

    const handleFocus = () => {
      if (!currentValue) {
        handleValueChange('+');
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      if (shouldPreventBackspaceAtPlus(e.key, selectionStart, selectionEnd)) {
        e.preventDefault();
        return;
      }

      if (shouldPreventDeleteAtStart(e.key, selectionStart, selectionEnd)) {
        e.preventDefault();
        return;
      }

      if (shouldHandleSelectionWithPlus(e.key, selectionStart, selectionEnd)) {
        e.preventDefault();
        const newValue = '+' + currentValue.slice(selectionEnd);
        handleValueChange(newValue || '+');
        setSelectionWithDelay(target, 1);
      }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const target = e.target;

      if (!newValue.startsWith('+') && currentValue.startsWith('+')) {
        const newDigits = newValue.replace(/\D/g, '');
        const currentDigits = currentValue.replace(/\D/g, '');

        if (newDigits.length > currentDigits.length) {
          const addedDigits = newDigits.slice(
            0,
            newDigits.length - currentDigits.length,
          );
          const result = currentValue + addedDigits;
          handleValueChange(result);
          setSelectionWithDelay(target, result.length);
        }
        return;
      }

      handleChange(e);
    };

    const displayError = error || (showError && validationError);

    return (
      <div className={styles.root}>
        <input
          id={PHONE_INPUT_NODE_ID}
          className={classes([styles.input, displayError && styles.hasError])}
          type="tel"
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyPress}
          onPaste={handlePaste}
          onFocus={handleFocus}
          placeholder={PHONE_INPUT_PLACEHOLDER}
          ref={ref}
        />
        {displayError && <div className={styles.error}>{displayError}</div>}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
